import type { CursorPage, Relationship, RelationshipActivity, RelationshipDetailResponse } from '@mira/clients-shared';
import { useState, type FormEvent } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { Avatar, Button, EmptyState, ErrorState, IconGlyph, MiraIcon, SegmentedTabs } from '../../ui/primitives/index.js';
import { DangerDialog, DetailTopbar, FormRow, ListState } from '../../ui/patterns/index.js';
import { RelationshipRow } from '../../ui/domain/index.js';
import { useApiQuery } from '../api-state.js';
import { sampleActivities, sampleRelationships } from '../reference-preview-data.js';
import { formatDateLabel, page } from '../shared.js';

const sarahPreviewRelationship: Relationship = sampleRelationships.find((relationship) => relationship.id === 'sarah') || { id: 'sarah', display_name: 'Sarah Liu', relationship_text: 'Sister', gender: 'female' };
const emptyRelationship: Relationship = { id: 'new', display_name: '', relationship_text: '', gender: 'unknown' };


function emptyRelationshipForId(relationshipId: string): Relationship {
  return { ...emptyRelationship, id: relationshipId };
}

export function RelationshipsScreen(props: { preview?: boolean; screenId?: string }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState(props.screenId === 'ls_05_relationships_search_no_results' ? 'Sam' : '');
  const previewState = relationshipsPreviewState(props.screenId);
  const relationships = useApiQuery<CursorPage<Relationship>>({
    preview: Boolean(props.preview),
    previewData: page(sampleRelationships),
    previewError: 'Relationships could not be loaded right now. Please try again.',
    previewState,
    load: () => createWebApiClient().relationships({ limit: 30 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [],
  });
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'family', label: 'Family' },
    { id: 'work', label: 'Work' },
    { id: 'friend', label: 'Friends' },
    { id: 'self', label: 'Myself' },
  ];
  const visibleRelationships = relationships.data.items.filter((relationship) => {
    const query = search.trim().toLowerCase();
    const matchesSearch = !query || `${relationship.display_name} ${relationship.relationship_text || ''}`.toLowerCase().includes(query);
    const matchesFilter = filter === 'all' || relationshipGroup(relationship) === filter;
    return matchesSearch && matchesFilter;
  });
  const createHref = hrefForRoute('relationship-new', {}, search.trim() ? { displayName: search.trim() } : {});

  return (
    <article className="mira-business-screen mira-relationships-screen">
      <header className="mira-relationships-header">
        <div className="mira-main-brand-row">
          <span className="mira-main-brand">Mira<span aria-hidden="true"><IconGlyph value="⌁" /></span></span>
          <a className="mira-header-round-icon" href={hrefForRoute('relationship-new')} aria-label="Add relationship"><MiraIcon name="semantic.relationship" /></a>
        </div>
        <h1>Relationships</h1>
        <p>Keep the people who matter and their real context here, so Mira can understand your relationships more accurately in conversation.</p>
      </header>
      <SegmentedTabs buttonClassName="mira-filter-chip" className="mira-filter-chips" value={filter} items={filters} onChange={setFilter} />
      <label className="mira-topic-search-wrap">
        <span aria-hidden="true"><IconGlyph value="⌕" /></span>
        <input aria-label="Search relationships" onChange={(event) => setSearch(event.currentTarget.value)} placeholder="Search by name or relationship" type="search" value={search} />
      </label>
      <a className="mira-create-relationship-card" href={createHref}>
        <span aria-hidden="true"><IconGlyph value="＋" /></span>
        <div>
          <strong>Add a relationship</strong>
          <p>Record someone who matters to you.</p>
        </div>
      </a>
      {props.screenId === 'rm_08_delete_success' ? <p className="mira-soft-note">Relationship removed. Past conversations remain as independent history.</p> : null}
      <ListState
        state={relationships.state}
        emptyTitle="No relationships yet"
        emptyBody="After you add someone who matters, Mira uses this real context in relevant conversations."
        emptyActionHref={createHref}
        emptyActionLabel="Add relationship"
        errorTitle={relationships.error || 'Relationships could not be loaded'}
        errorBody="Please try again later, or return to Today first."
        errorActionHref={hrefForRoute('today')}
        errorActionLabel="Back to Today"
      >
        {visibleRelationships.length > 0 ? (
          <div className="mira-relationship-visual-list">
            {visibleRelationships.map((relationship) => (
              <RelationshipRow
                href={hrefForRoute('relationship-detail', { relationshipId: relationship.id })}
                key={relationship.id}
                relationship={relationship}
              />
            ))}
          </div>
        ) : (
          <div className="mira-empty-action-stack">
            <EmptyState title={search.trim() ? `No matches for “${search.trim()}”` : 'No relationships match'} />
            <a href={createHref}>{search.trim() ? `Add “${search.trim()}”` : 'Add relationship'}</a>
          </div>
        )}
      </ListState>
    </article>
  );
}

function relationshipsPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'ls_10_relationships_loading') return 'loading';
  if (screenId === 'ls_04_relationships_empty' || screenId === 'rm_10_delete_last_empty') return 'empty';
  if (screenId === 'ls_06_relationships_api_error') return 'error';
  return undefined;
}

function relationshipGroup(relationship: Relationship): string {
  const text = `${relationship.display_name} ${relationship.relationship_text || ''}`.toLowerCase();
  if (/(family|mom|dad|son|sister|家人|父|母|兄|弟|姐|妹|孩子)/u.test(text)) return 'family';
  if (/(teammate|work|colleague|同事|工作|客户|上司|下属)/u.test(text)) return 'work';
  if (/(self|自己)/u.test(text)) return 'self';
  return 'friend';
}

export function RelationshipDetailScreen(props: { preview?: boolean; relationshipId: string; screenId?: string; showDelete?: boolean }) {
  const [actionError, setActionError] = useState(props.screenId === 'rm_09_delete_failed' ? 'This relationship could not be deleted right now. Please try again.' : '');
  const deleteBusy = props.preview && props.screenId === 'rm_13_delete_deleting';
  const previewRelationship = props.relationshipId === 'sarah'
    ? sarahPreviewRelationship
    : sampleRelationships.find((relationship) => relationship.id === props.relationshipId) || sampleRelationships[0];
  const detail = useApiQuery<RelationshipDetailResponse>({
    preview: Boolean(props.preview),
    previewData: {
      relationship: previewRelationship,
      activities: sampleActivities,
      aliases: [],
      profile: null,
    },
    emptyData: { relationship: emptyRelationshipForId(props.relationshipId), activities: [], aliases: [], profile: null },
    load: () => createWebApiClient().relationship(props.relationshipId),
    deps: [props.relationshipId],
  });
  const relationship = detail.data.relationship;

  async function deleteRelationship() {
    if (props.preview) return;
    try {
      setActionError('');
      await createWebApiClient().deleteRelationship(props.relationshipId);
      showToast('Relationship deleted.');
      navigateTo('relationships');
    } catch {
      setActionError('This relationship could not be deleted right now. Please try again.');
    }
  }

  return (
    <article className="mira-business-screen mira-relationship-detail-screen">
      <DetailTopbar
        backHref={hrefForRoute('relationships')}
        backLabel="Relationships"
        label="Mira"
        menuLabel="Delete relationship"
        onMenu={() => navigateTo('relationship-detail', { relationshipId: props.relationshipId }, { sheet: 'delete' })}
      />
      <ListState state={detail.state} errorTitle={detail.error || 'Relationships could not be loaded'} errorBody="This relationship could not be opened right now. Please try again later." errorActionHref={hrefForRoute('relationships')} errorActionLabel="Back to relationships">
        {props.screenId === 'rm_05_save_success' ? <p className="mira-soft-note">Relationship saved.</p> : null}
        <RelationshipProfileHero relationship={relationship} />
        <RelationshipKnowledgeCard detail={detail.data} />
        <RelationshipInteractionsCard relationship={relationship} activities={detail.data.activities || []} />
        <a
          className="mira-relationship-chat-button"
          href={hrefForRoute('chat', { conversationId: 'new' }, {
            source: 'relationship-detail',
            relationshipId: relationship.id,
            returnTo: hrefForRoute('relationship-detail', { relationshipId: relationship.id }).slice(1),
          })}
        ><IconGlyph value="☰" /> Chat with Mira about {relationship.display_name}</a>
        <div className="mira-relationship-detail-actions">
          <a href={hrefForRoute('relationship-new', {}, { relationshipId: relationship.id })}><IconGlyph value="✎" /> Edit relationship</a>
          <button onClick={() => navigateTo('relationship-detail', { relationshipId: props.relationshipId }, { sheet: 'delete' })} type="button"><IconGlyph value="⌫" /> Delete relationship</button>
        </div>
      </ListState>
      {actionError ? <ErrorState title="Action failed" body={actionError} /> : null}
      {props.showDelete ? (
        <DangerDialog
          body={(
            <>
              <p>This removes the relationship from Mira.</p>
              <ul>
                <li>The profile you recorded and the relationship understanding about {relationship.display_name} will be deleted.</li>
                <li>Past conversations remain as independent history.</li>
              </ul>
            </>
          )}
          busy={deleteBusy}
          compact
          confirmLabel="Delete"
          confirmLoadingLabel="Deleting…"
          onCancel={() => navigateTo('relationship-detail', { relationshipId: props.relationshipId })}
          onConfirm={deleteRelationship}
          preview={<RelationshipMiniCard relationship={relationship} />}
          title={`Delete ${relationship.display_name}?`}
        />
      ) : null}
    </article>
  );
}

export function RelationshipFormScreen(props: { initialName?: string | null; preview?: boolean; relationshipId?: string | null; screenId?: string }) {
  const previewSaving = props.preview && (props.screenId === 'rm_11_add_saving' || props.screenId === 'rm_12_edit_saving');
  const previewFailed = props.preview && props.screenId === 'rm_06_save_failed';
  const [error, setError] = useState(previewFailed ? 'The relationship could not be saved right now. Please try again.' : '');
  const [saving, setSaving] = useState(previewSaving);
  const editing = Boolean(props.relationshipId);
  const detail = useApiQuery<RelationshipDetailResponse>({
    preview: Boolean(props.preview),
    previewData: {
      relationship: sampleRelationships[0],
      activities: [],
      aliases: [],
      birthProfile: null,
      profile: null,
    },
    emptyData: { relationship: props.relationshipId ? emptyRelationshipForId(props.relationshipId) : emptyRelationship, activities: [], aliases: [], birthProfile: null, profile: null },
    load: () => props.relationshipId
      ? createWebApiClient().relationship(props.relationshipId)
      : Promise.resolve({ relationship: emptyRelationship, activities: [], aliases: [], birthProfile: null, profile: null }),
    deps: [props.relationshipId],
  });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const relationshipContext = String(form.get('relationshipContext') || '').trim() || null;
    setSaving(true);
    setError('');
    try {
      if (props.relationshipId) {
        const birthDate = String(form.get('birthDate') || '');
        const response = await createWebApiClient().updateRelationship(props.relationshipId, {
          displayName: String(form.get('displayName') || ''),
          relationshipText: String(form.get('relationshipText') || ''),
          relationshipContext,
          gender: String(form.get('gender') || 'unknown') as 'female' | 'male' | 'other' | 'unknown',
          ...(birthDate ? relationshipBirthProfileFromForm(form, birthDate) : { birthDate: null, birthHour: null, birthMinute: null }),
        });
        finishRelationshipSave(response.relationship.id, String(form.get('intent') || 'save'));
      } else {
        const birthDate = String(form.get('birthDate') || '');
        const response = await createWebApiClient().createRelationship({
          displayName: String(form.get('displayName') || ''),
          relationshipText: String(form.get('relationshipText') || ''),
          relationshipContext,
          gender: String(form.get('gender') || 'unknown') as 'female' | 'male' | 'other' | 'unknown',
          ...(birthDate ? relationshipBirthProfileFromForm(form, birthDate) : { birthDate: null }),
        });
        finishRelationshipSave(response.relationship.id, String(form.get('intent') || 'save'));
      }
    } catch {
      setError('The relationship could not be saved. Please check your entries and try again.');
    } finally {
      setSaving(false);
    }
  }

  function finishRelationshipSave(relationshipId: string, intent: string) {
    if (intent === 'chat') {
      showToast('Relationship saved.');
      navigateTo('chat', { conversationId: 'new' }, {
        relationshipId,
        returnTo: hrefForRoute('relationship-detail', { relationshipId }).slice(1),
        source: 'relationship-detail',
      });
      return;
    }
    showToast('Relationship saved.');
    navigateTo('relationship-detail', { relationshipId });
  }

  const relationship = detail.data.relationship;

  return (
    <article className="mira-business-screen mira-relationship-form-screen">
      <header className="mira-relationship-form-header">
        <a className="mira-round-back" href={hrefForRoute('relationships')} aria-label="Back"><MiraIcon name="action.back" /></a>
        <h1>{editing ? 'Edit relationship' : 'Add relationship'}</h1>
        <p>Only fill in facts you are sure about; leave birth time blank if unsure.</p>
        <button aria-disabled="true" className="mira-relationship-photo-button" disabled type="button">
          <span aria-hidden="true"><IconGlyph value="♙" /></span>
          <strong aria-hidden="true"><IconGlyph value="＋" /></strong>
        </button>
        <small>Avatar upload coming later</small>
      </header>
      <ListState state={detail.state} errorTitle={detail.error || 'Relationships could not be loaded'} errorBody="This relationship could not be opened right now. Please try again later." errorActionHref={hrefForRoute('relationships')} errorActionLabel="Back to relationships">
      <form className="mira-relationship-form" key={relationship.id} onSubmit={submit}>
        <section className="mira-relationship-form-card">
          <RelationshipInputRow defaultValue={editing ? relationship.display_name : props.initialName || ''} icon="♙" label="Name" name="displayName" placeholder="What do you call them" required />
          <RelationshipInputRow defaultValue={editing ? relationship.relationship_text || '' : ''} icon="♡" label="Relationship" name="relationshipText" placeholder="e.g. Partner, sister, colleague" />
          <FormRow className="mira-relationship-form-row" icon="♙" label="Gender (optional)">
            <select aria-label="Gender (optional)" defaultValue={editing ? relationship.gender || 'unknown' : 'unknown'} name="gender">
              <option value="unknown">Prefer not to say</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
            </select>
          </FormRow>
          <FormRow className="mira-relationship-form-row" icon="☰" label="Background (optional)">
            <textarea aria-label="Background (optional)" defaultValue={editing ? relationship.relationship_context || '' : ''} maxLength={500} name="relationshipContext" placeholder="e.g. Lately trying to communicate more directly" />
          </FormRow>
          <RelationshipInputRow defaultValue={editing ? detail.data.birthProfile?.birthDate || detail.data.birthProfile?.birth_date || '' : ''} icon="▣" label="Birth date (optional)" name="birthDate" placeholder="Pick a date" type="date" />
          <RelationshipSelectRow defaultValue={String(detail.data.birthProfile?.birthHour ?? detail.data.birthProfile?.birth_hour ?? '')} label="Birth hour (optional)" name="birthHour" options={hourOptions()} />
          <RelationshipSelectRow defaultValue={String(detail.data.birthProfile?.birthMinute ?? detail.data.birthProfile?.birth_minute ?? '')} label="Birth minute (optional)" name="birthMinute" options={minuteOptions()} />
          <RelationshipSelectRow defaultValue={detail.data.birthProfile?.timezone || 'Asia/Shanghai'} label="Time zone" name="timezone" options={timezoneOptions()} />
          <RelationshipInputRow defaultValue={editing ? detail.data.birthProfile?.birthPlace || detail.data.birthProfile?.birth_place || '' : ''} icon="⌖" label="Birth place (optional)" name="birthPlace" placeholder="City, province, country" />
        </section>
        {error || previewFailed ? <ErrorState title="Save failed" body={error || 'The relationship could not be saved right now. Please try again.'} /> : null}
        <footer className="mira-relationship-form-dock">
          <Button busy={saving || previewSaving} disabled={saving || previewSaving} name="intent" type="submit" value="save">{saving || previewSaving ? 'Saving…' : 'Save relationship'}</Button>
        </footer>
      </form>
      </ListState>
    </article>
  );
}

function RelationshipProfileHero(props: { relationship: Relationship }) {
  return (
    <header className="mira-relationship-profile-hero">
      <Avatar label={props.relationship.display_name} />
      <div>
        <h1>{props.relationship.display_name}</h1>
        <span><IconGlyph value="♙" /> {props.relationship.relationship_text || 'Important relationship'}</span>
        <p>{props.relationship.relationship_context || 'No background recorded yet.'}</p>
      </div>
    </header>
  );
}

function RelationshipKnowledgeCard(props: { detail: RelationshipDetailResponse }) {
  const items = relationshipKnowledge(props.detail);
  return (
    <section className="mira-relationship-card-block mira-relationship-knowledge-card">
      <header><h2>What Mira knows</h2><a href={hrefForRoute('relationship-new', {}, { relationshipId: props.detail.relationship.id })}>Edit</a></header>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </section>
  );
}

function relationshipKnowledge(detail: RelationshipDetailResponse): string[] {
  const relationship = detail.relationship;
  const birth = detail.birthProfile;
  const profile = detail.profile || {};
  const summary = typeof profile.summary === 'string' ? profile.summary.trim() : '';
  const items = [
    relationship.relationship_text ? `Relationship: ${relationship.relationship_text}` : '',
    relationship.relationship_context ? `Background: ${relationship.relationship_context}` : '',
    birth?.birthDate || birth?.birth_date ? `Birth: ${birth.birthDate || birth.birth_date} (${birthPrecisionLabel(birth.birthTimePrecision || birth.birth_time_precision)})` : '',
    summary ? `Understanding: ${summary}` : '',
  ].filter(Boolean);
  return items.length ? items : ['Not enough relationship facts yet. Edit the profile, or start from a real conversation.'];
}

function RelationshipInteractionsCard(props: { activities: RelationshipActivity[]; relationship: Relationship }) {
  const rows = props.activities.slice(0, 2).map((activity, index) => ({
    body: activity.content,
    meta: relationshipActivityMeta(activity, index),
    icon: ['☰', '♡'][index] || '☰',
    title: activity.content,
  }));
  return (
    <section className="mira-relationship-card-block">
      <header><h2>Recent relationship threads</h2><a href={hrefForRoute('topics')}>View all</a></header>
      {rows.length > 0 ? rows.map((row) => (
        <a className="mira-relationship-interaction-row" href={hrefForRoute('chat', { conversationId: 'new' }, { source: 'relationship-detail', relationshipId: props.relationship.id })} key={`${row.title}-${row.meta}`}>
          <span aria-hidden="true"><IconGlyph value={row.icon} /></span>
          <div><strong>{row.title}</strong><p>{row.meta}</p></div>
          <span className="mira-row-chevron" aria-hidden="true"><IconGlyph value="›" /></span>
        </a>
      )) : <EmptyState title="Nothing here yet" body={`Chat with Mira about ${props.relationship.display_name}; real threads will appear here once they form.`} />}
    </section>
  );
}

function relationshipActivityMeta(activity: RelationshipActivity, index: number): string {
  return `${activity.created_at ? formatDateLabel(activity.created_at) : index === 0 ? 'Recent' : 'Earlier'} · ${activity.activity_kind === 'conversation' ? 'Conversation' : relationshipActivityTitle(activity)}`;
}

function relationshipActivityTitle(activity: RelationshipActivity): string {
  if (activity.activity_kind === 'memory') return 'Formed a suggested memory';
  if (activity.activity_kind === 'insight') return 'Formed an insight';
  return 'A meaningful conversation';
}

function RelationshipMiniCard(props: { relationship: Relationship }) {
  return (
    <div className="mira-relationship-mini-card">
      <Avatar label={props.relationship.display_name} />
      <div>
        <strong>{props.relationship.display_name}</strong>
        <span>{props.relationship.relationship_text || 'Important relationship'}  · your relationship</span>
      </div>
    </div>
  );
}

function RelationshipInputRow(props: {
  defaultValue: string;
  icon: string;
  label: string;
  meta?: string;
  name: string;
  placeholder: string;
  required?: boolean;
  type?: string;
}) {
  return (
    <FormRow className="mira-relationship-form-row" icon={props.icon} label={props.label} meta={props.meta} required={props.required}>
      <input aria-label={props.label} defaultValue={props.defaultValue} name={props.name} placeholder={props.placeholder} required={props.required} type={props.type || 'text'} />
    </FormRow>
  );
}

function RelationshipSelectRow(props: { defaultValue: string; label: string; name: string; options: Array<{ value: string; label: string }> }) {
  return (
    <FormRow className="mira-relationship-form-row" icon="◷" label={props.label}>
      <select aria-label={props.label} defaultValue={props.defaultValue} name={props.name}>
        {props.options.map((option) => <option key={option.value || 'empty'} value={option.value}>{option.label}</option>)}
      </select>
    </FormRow>
  );
}

function hourOptions() {
  return [{ value: '', label: 'Unknown' }, ...Array.from({ length: 24 }, (_, hour) => ({ value: String(hour), label: `${String(hour).padStart(2, '0')}h` }))];
}

function minuteOptions() {
  return [{ value: '', label: 'Unknown' }, ...Array.from({ length: 60 }, (_, minute) => ({ value: String(minute), label: `${String(minute).padStart(2, '0')}m` }))];
}

function timezoneOptions() {
  return [
    { value: 'Asia/Shanghai', label: 'China Standard Time (Asia/Shanghai)' },
    { value: 'America/New_York', label: 'US Eastern (America/New_York)' },
    { value: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
    { value: 'Europe/London', label: 'UK Time (Europe/London)' },
    { value: 'Asia/Tokyo', label: 'Japan Time (Asia/Tokyo)' },
  ];
}

function birthPrecisionLabel(value: unknown) {
  if (value === 'minute_known') return 'Minute known';
  if (value === 'hour_known') return 'Hour known';
  return 'Date only';
}

function relationshipBirthProfileFromForm(form: FormData, birthDate: string) {
  return {
    birthDate,
    birthHour: optionalNumber(form.get('birthHour')),
    birthMinute: optionalNumber(form.get('birthMinute')),
    timezone: String(form.get('timezone') || 'Asia/Shanghai'),
    birthPlace: String(form.get('birthPlace') || '') || null,
  };
}

function optionalNumber(value: FormDataEntryValue | null): number | null {
  const text = String(value || '');
  return text === '' ? null : Number(text);
}
